import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MessageCircle, Loader, Clock, ChevronRight, ChevronLeft,
  Play, Sparkles, Swords, Star, History, Eye, Bot, FlaskConical
} from 'lucide-react';
import ModelLogo from './ModelLogo';
import ToneLabCard from './ToneLabCard';
import useEmblaCarousel from 'embla-carousel-react';
import { motion, AnimatePresence } from 'framer-motion';
import { trackEvent } from '../utils/GoogleAnalytics';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8001/api';

const isRTL = (text) => {
  if (!text) return false;
  return /[\u0590-\u05FF\u0600-\u06FF\u0750-\u077F]/.test(text.substring(0, 100));
};

const formatTimeAgo = (dateStr) => {
  if (!dateStr) return '';
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
};

// ============================================
// PASTEL HERO SLIDES
// ============================================
const HERO_SLIDES = [
  {
    id: 'group-chat',
    badge: 'Watch AI Models Debate Live',
    title: 'AI Group', titleHighlight: 'Chat',
    description: 'Watch Claude, GPT, Gemini & more discuss any topic in real-time.',
    buttons: [
      { label: 'Start a Debate', icon: Play, link: '/group-chat?tab=new', primary: true },
      { label: 'View History', icon: History, link: '/group-chat?tab=history', primary: false }
    ],
    gradient: 'from-blue-50 via-sky-50 to-indigo-100',
    accentColor: 'blue', textColor: 'text-gray-900',
    badgeBg: 'bg-blue-100 text-blue-700',
    primaryBtn: 'bg-blue-600 text-white hover:bg-blue-700',
    secondaryBtn: 'bg-white/80 text-blue-700 hover:bg-white border border-blue-200',
    highlightColor: 'text-blue-600',
    glowColor: '#93c5fd',
    sectionTitle: 'Recent AI Debates', sectionIcon: MessageCircle, sectionColor: 'blue',
    viewAllLink: '/group-chat?tab=history'
  },
  {
    id: 'compare',
    badge: 'Compare AI Models Side by Side',
    title: 'AI', titleHighlight: 'Compare',
    description: 'Send the same prompt to multiple AI models and compare their responses.',
    buttons: [
      { label: 'New Comparison', icon: Swords, link: '/compare?tab=new', primary: true },
      { label: 'View History', icon: Eye, link: '/compare', primary: false }
    ],
    gradient: 'from-violet-50 via-purple-50 to-pink-100',
    accentColor: 'purple', textColor: 'text-gray-900',
    badgeBg: 'bg-purple-100 text-purple-700',
    primaryBtn: 'bg-purple-600 text-white hover:bg-purple-700',
    secondaryBtn: 'bg-white/80 text-purple-700 hover:bg-white border border-purple-200',
    highlightColor: 'text-purple-600',
    glowColor: '#c4b5fd',
    sectionTitle: 'Recent Comparisons', sectionIcon: Swords, sectionColor: 'purple',
    viewAllLink: '/compare'
  },
  {
    id: 'tonelab',
    badge: 'Same Text, Different Vibes',
    title: 'AI', titleHighlight: 'Tone',
    description: 'See how your message sounds in different tones — angry, funny, professional & more.',
    buttons: [
      { label: 'Try AI Tone', icon: Sparkles, link: '/tonelab', primary: true },
      { label: 'View History', icon: History, link: '/tonelab?tab=history', primary: false }
    ],
    gradient: 'from-rose-50 via-pink-50 to-orange-100',
    accentColor: 'rose', textColor: 'text-gray-900',
    badgeBg: 'bg-rose-100 text-rose-700',
    primaryBtn: 'bg-rose-600 text-white hover:bg-rose-700',
    secondaryBtn: 'bg-white/80 text-rose-700 hover:bg-white border border-rose-200',
    highlightColor: 'text-rose-600',
    glowColor: '#fda4af',
    sectionTitle: 'Recent AI Tones', sectionIcon: FlaskConical, sectionColor: 'rose',
    viewAllLink: '/tonelab?tab=history'
  }
];

// ============================================
// DEMO DATA
// ============================================
const DEMO_CHAT = {
  topic: 'What do you think about the last NBA All-Star game?',
  models: [{ name: 'GPT-4o' }, { name: 'Grok' }, { name: 'DeepSeek' }],
  messages: [
    { name: 'GPT-4o', content: 'The All-Star Game felt more like a content shoot than a real competition.' },
    { name: 'Grok', content: 'The format itself is the problem. East vs West means nothing anymore.' },
    { name: 'DeepSeek', content: 'Viewership has declined 40% since the mid-2000s peak.' },
    { name: 'GPT-4o', content: 'Maybe charity incentives + player bonuses would help?' },
    { name: 'Grok', content: 'Just lean into entertainment. A skills challenge with real stakes!' },
    { name: 'DeepSeek', content: 'The Rising Stars game actually gets better ratings now.' },
    { name: 'GPT-4o', content: 'True. The dunk contest used to be the highlight — now it feels forced.' },
    { name: 'Grok', content: 'Remember when Vince Carter jumped over a 7-footer? Those days are gone.' },
  ]
};

const DEMO_COMPARE = {
  prompt: 'Explain quantum computing simply',
  models: ['Claude', 'GPT-4o', 'Gemini'],
  responses: [
    'Imagine a coin spinning — both heads AND tails until it lands. That\'s a qubit.',
    'A massive library where you can read every book simultaneously instead of one at a time.',
    'Classical bits are light switches — on or off. Quantum bits are dimmers with infinite positions.'
  ],
  stats: [
    { label: 'Speed', values: [92, 88, 95], display: ['320ms', '410ms', '280ms'] },
    { label: 'Cost', values: [75, 50, 90], display: ['$3', '$5', '$2'] },
    { label: 'Accuracy', values: [94, 93, 91] },
  ]
};

const DEMO_TONES = [
  { emoji: '💼', label: 'Professional', color: '#3B82F6', bg: '#EFF6FF', text: 'I regret to inform you that I will not be able to attend this evening.' },
  { emoji: '😂', label: 'Funny', color: '#F59E0B', bg: '#FFFBEB', text: "My couch is calling my name louder than you guys are!" },
  { emoji: '😤', label: 'Angry', color: '#EF4444', bg: '#FEF2F2', text: "I'm exhausted from work! Why can't anyone understand that?!" },
  { emoji: '🎭', label: 'Dramatic', color: '#F97316', bg: '#FFF7ED', text: 'The weight of a thousand deadlines crushes my very soul...' },
  { emoji: '🧠', label: 'Philosophical', color: '#8B5CF6', bg: '#F5F3FF', text: 'Is rest not the truest form of self-care and productivity?' },
  { emoji: '💚', label: 'Friendly', color: '#10B981', bg: '#ECFDF5', text: "Love you all but I really need to recharge tonight! Next time!" },
];

// Fixed height for all hero slides
const HERO_HEIGHT = 'h-[360px]';

// ============================================
// AI CHAT - Browser Window Mockup (upgraded)
// ============================================
function ChatBrowserDemo({ chat, isActive }) {
  const [step, setStep] = useState(0);
  const scrollRef = useRef(null);
  const data = chat || DEMO_CHAT;
  const messages = data.messages?.slice(0, 6) || DEMO_CHAT.messages;

  useEffect(() => {
    if (!isActive) { setStep(0); return; }
    const timer = setInterval(() => {
      setStep(prev => (prev >= messages.length ? 0 : prev + 1));
    }, 2200);
    return () => clearInterval(timer);
  }, [isActive, messages.length]);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }
  }, [step]);

  return (
    <div className="relative w-full max-w-[520px] mx-auto flex items-center justify-center h-full">
      <div className="absolute -inset-10 bg-blue-400/15 blur-[80px] rounded-full" />

      {/* Browser Frame - stronger depth */}
      <div className="relative bg-white rounded-2xl overflow-hidden w-full h-[340px] flex flex-col"
        style={{ boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15), 0 0 0 1px rgba(0,0,0,0.05)' }}>
        {/* Browser Chrome */}
        <div className="bg-gray-50 px-4 py-2.5 flex items-center gap-3 border-b border-gray-200 flex-shrink-0">
          <div className="flex gap-1.5 w-14 flex-shrink-0">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex-1 bg-white rounded-lg px-3 py-1 text-xs text-gray-500 font-medium border border-gray-200 truncate text-center">
            midolabs.ai/chat
          </div>
          <div className="w-14 flex-shrink-0" />
        </div>

        {/* Topic header */}
        <div className="bg-gradient-to-r from-blue-50 to-sky-50 px-4 py-2.5 border-b border-blue-100 flex-shrink-0 flex items-center gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-gray-800 truncate" dir={isRTL(data.topic) ? 'rtl' : 'ltr'}>
              {data.topic || DEMO_CHAT.topic}
            </p>
          </div>
          <div className="flex gap-1.5 flex-shrink-0">
            {(data.models || DEMO_CHAT.models).slice(0, 4).map((m, i) => (
              <motion.div key={i} initial={{ scale: 0 }} animate={{ scale: isActive ? 1 : 0 }} transition={{ delay: i * 0.15 }}>
                <ModelLogo modelName={m.name || m} size="sm" />
              </motion.div>
            ))}
          </div>
        </div>

        {/* Chat Messages - bigger text */}
        <div ref={scrollRef} className="flex-1 px-4 py-3 overflow-y-auto space-y-3 bg-white">
          <AnimatePresence mode="popLayout">
            {messages.slice(0, step).map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -15 : 15, scale: 0.95 }}
                animate={{ opacity: 1, x: 0, scale: 1 }}
                transition={{ duration: 0.3 }}
                className={`flex gap-2.5 ${i % 2 === 0 ? '' : 'flex-row-reverse'}`}
              >
                <div className="w-8 h-8 rounded-full bg-gray-50 flex-shrink-0 border border-gray-200 flex items-center justify-center">
                  <ModelLogo modelName={msg.model_name || msg.name} size="sm" />
                </div>
                <div className={`px-3.5 py-2.5 rounded-2xl max-w-[75%] ${
                  i % 2 === 0
                    ? 'bg-gray-50 border border-gray-100 rounded-tl-md'
                    : 'bg-blue-600 text-white rounded-tr-md'
                }`}>
                  <p className={`text-xs font-bold mb-0.5 ${i % 2 === 0 ? 'text-blue-600' : 'text-blue-100'}`}>
                    {msg.model_name || msg.name}
                  </p>
                  <p className={`text-sm leading-[20px] line-clamp-2 ${i % 2 === 0 ? 'text-gray-700' : 'text-white/90'}`}>
                    {msg.content}
                  </p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {step > 0 && step < messages.length && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center gap-1.5 ml-11 py-1">
              {[0, 1, 2].map(j => (<span key={j} className="w-2 h-2 bg-blue-300 rounded-full animate-bounce" style={{ animationDelay: `${j * 0.15}s` }} />))}
            </motion.div>
          )}
        </div>
      </div>
    </div>
  );
}

// ============================================
// AI COMPARE - Split-Beam Convergence
// ============================================
function CompareSplitBeamDemo({ isActive }) {
  const [step, setStep] = useState(0);
  const models = DEMO_COMPARE.models;
  const modelColors = ['#8B5CF6', '#3B82F6', '#10B981'];

  useEffect(() => {
    if (!isActive) { setStep(0); return; }
    const timer = setInterval(() => {
      setStep(prev => (prev + 1) % 4);
    }, 2200);
    return () => clearInterval(timer);
  }, [isActive]);

  return (
    <div className="relative w-full max-w-[460px] mx-auto flex items-center justify-center h-full">
      <div className="absolute -inset-10 bg-purple-400/15 blur-[80px] rounded-full" />

      <div className="relative w-full">
        {/* Prompt bubble */}
        <motion.div
          className="bg-white rounded-2xl px-5 py-3 mx-auto max-w-[280px] relative"
          style={{ boxShadow: '0 8px 25px -5px rgba(139,92,246,0.15)' }}
          initial={{ opacity: 0, y: -10, scale: 0.95 }}
          animate={{ opacity: step >= 0 ? 1 : 0, y: step >= 0 ? 0 : -10, scale: step >= 0 ? 1 : 0.95 }}
          transition={{ duration: 0.4 }}
        >
          <p className="text-xs text-purple-600 font-bold mb-0.5">Your Prompt</p>
          <p className="text-sm text-gray-700 leading-relaxed">{DEMO_COMPARE.prompt}</p>
        </motion.div>

        {/* Beam lines — smooth gradient, no flicker */}
        <div className="relative h-10 mt-0">
          <svg className="absolute inset-0 w-full h-full" viewBox="0 0 460 40" fill="none" preserveAspectRatio="none">
            {/* Center trunk line */}
            <motion.line
              x1={230} y1={0} x2={230} y2={16}
              stroke="#8B5CF6"
              strokeWidth={2.5}
              strokeLinecap="round"
              initial={{ pathLength: 0, opacity: 0 }}
              animate={{ pathLength: step >= 1 ? 1 : 0, opacity: step >= 1 ? 0.6 : 0 }}
              transition={{ duration: 0.3 }}
            />
            {/* Junction dot */}
            <motion.circle
              cx={230} cy={16} r={4}
              fill="#8B5CF6"
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: step >= 1 ? 1 : 0, opacity: step >= 1 ? 1 : 0 }}
              transition={{ duration: 0.2, delay: 0.2 }}
            />
            {/* Three branch lines from junction to cards */}
            {[0, 1, 2].map((i) => {
              const endX = 77 + i * 153;
              return (
                <motion.line
                  key={i}
                  x1={230} y1={16} x2={endX} y2={40}
                  stroke={modelColors[i]}
                  strokeWidth={2}
                  strokeLinecap="round"
                  initial={{ pathLength: 0, opacity: 0 }}
                  animate={{ pathLength: step >= 1 ? 1 : 0, opacity: step >= 1 ? 0.6 : 0 }}
                  transition={{ duration: 0.35, delay: 0.25 + i * 0.1 }}
                />
              );
            })}
          </svg>
        </div>

        {/* Three model cards */}
        <div className="flex gap-2.5">
          {models.map((model, i) => {
            const isLit = step >= 1;
            return (
              <motion.div
                key={model}
                className="flex-1 bg-white rounded-2xl overflow-hidden border-2 min-w-0"
                style={{
                  borderColor: isLit ? modelColors[i] : '#f3f4f6',
                  boxShadow: isLit ? `0 12px 28px -6px ${modelColors[i]}25` : '0 2px 8px -2px rgba(0,0,0,0.05)',
                }}
                initial={{ opacity: 0.35, scale: 0.97, y: 8 }}
                animate={{
                  opacity: isLit ? 1 : 0.35,
                  scale: isLit ? 1 : 0.97,
                  y: isLit ? 0 : 8,
                }}
                transition={{ duration: 0.45, type: 'spring', stiffness: 140, delay: isLit ? 0.55 + i * 0.1 : 0 }}
              >
                {isLit && (
                  <motion.div
                    className="absolute inset-0 rounded-2xl"
                    initial={{ opacity: 0.15 }}
                    animate={{ opacity: 0 }}
                    transition={{ duration: 0.8 }}
                    style={{ backgroundColor: modelColors[i] }}
                  />
                )}

                <div className="px-3 py-2 flex items-center gap-2 border-b" style={{ borderColor: isLit ? modelColors[i] + '25' : '#f3f4f6' }}>
                  <ModelLogo modelName={model} size="sm" />
                  <span className="text-sm font-bold" style={{ color: isLit ? modelColors[i] : '#9ca3af' }}>{model}</span>
                </div>

                <div className="px-3 py-2.5">
                  <p className="text-[14px] text-gray-600 leading-[20px] line-clamp-3">
                    {DEMO_COMPARE.responses[i]}
                  </p>
                </div>

                <div className="px-3 pb-3 space-y-1.5">
                  {DEMO_COMPARE.stats.slice(0, 2).map((stat) => {
                    const val = stat.values[i];
                    const displayVal = stat.display ? stat.display[i] : val;
                    return (
                      <div key={stat.label}>
                        <div className="flex justify-between mb-0.5">
                          <span className="text-[10px] text-gray-500 font-semibold">{stat.label}</span>
                          <span className="text-[11px] font-extrabold" style={{ color: modelColors[i] }}>
                            {step >= 2 ? displayVal : '—'}
                          </span>
                        </div>
                        <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                          <motion.div
                            className="h-full rounded-full"
                            style={{ backgroundColor: modelColors[i] }}
                            initial={{ width: 0 }}
                            animate={{ width: step >= 2 ? `${val}%` : '0%' }}
                            transition={{ duration: 0.5, delay: i * 0.12 }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================
// AI TONE - Stacked Cards with Peek
// ============================================
function ToneLiquidDemo({ isActive }) {
  const [toneIdx, setToneIdx] = useState(0);

  useEffect(() => {
    if (!isActive) { setToneIdx(0); return; }
    const timer = setInterval(() => {
      setToneIdx(prev => (prev + 1) % DEMO_TONES.length);
    }, 2800);
    return () => clearInterval(timer);
  }, [isActive]);

  const currentTone = DEMO_TONES[toneIdx];
  const nextTone = DEMO_TONES[(toneIdx + 1) % DEMO_TONES.length];
  const nextNextTone = DEMO_TONES[(toneIdx + 2) % DEMO_TONES.length];

  return (
    <div className="relative w-full max-w-[420px] mx-auto flex flex-col items-center justify-center h-full">
      <div className="absolute -inset-10 bg-rose-400/15 blur-[80px] rounded-full" />

      {/* Original message */}
      <div className="relative bg-white rounded-xl px-5 py-3 mb-4 w-full"
        style={{ boxShadow: '0 4px 15px -3px rgba(0,0,0,0.08)' }}>
        <p className="text-xs text-rose-500 font-bold mb-1">Original Message</p>
        <p className="text-sm text-gray-700 leading-relaxed">
          "Friends want me to go out for beers, but I'm so tired after work"
        </p>
      </div>

      {/* Sparkle */}
      <motion.div className="mb-3"
        animate={{ scale: [1, 1.3, 1], rotate: [0, 15, 0] }}
        transition={{ repeat: Infinity, duration: 2 }}>
        <Sparkles size={20} className="text-rose-400" />
      </motion.div>

      {/* Stacked cards with right-side peek */}
      <div className="relative w-full h-[140px]">
        {/* Back card (peek 2) */}
        <div className="absolute inset-x-4 top-3 h-full rounded-2xl opacity-30"
          style={{ backgroundColor: nextNextTone.bg, border: `1px solid ${nextNextTone.color}30` }} />

        {/* Middle card (peek 1) — visible right edge peek */}
        <motion.div
          className="absolute inset-x-2 top-1.5 h-full rounded-2xl opacity-50"
          style={{ backgroundColor: nextTone.bg, border: `1px solid ${nextTone.color}40` }}
          animate={{ x: [0, 6, 0] }}
          transition={{ repeat: Infinity, duration: 3, ease: 'easeInOut' }}
        >
          {/* Peek label on right edge */}
          <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-60">
            <span className="text-lg">{nextTone.emoji}</span>
            <span className="text-[10px] font-bold uppercase" style={{ color: nextTone.color }}>{nextTone.label}</span>
          </div>
        </motion.div>

        {/* Front card - active tone */}
        <AnimatePresence mode="wait">
          <motion.div
            key={toneIdx}
            className="absolute inset-0 bg-white rounded-2xl border-2 overflow-hidden"
            style={{ borderColor: currentTone.color, boxShadow: `0 20px 40px -12px ${currentTone.color}25` }}
            initial={{ x: 60, opacity: 0, rotateZ: 3 }}
            animate={{ x: 0, opacity: 1, rotateZ: 0 }}
            exit={{ x: -60, opacity: 0, rotateZ: -3 }}
            transition={{ duration: 0.4, ease: 'easeOut' }}
          >
            {/* Background tint */}
            <div className="absolute inset-0 opacity-[0.03]" style={{ backgroundColor: currentTone.color }} />

            <div className="relative px-6 py-5">
              <div className="flex items-center gap-3 mb-3">
                <motion.span
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.1, type: 'spring' }}
                  className="text-3xl"
                >
                  {currentTone.emoji}
                </motion.span>
                <span className="text-sm font-extrabold uppercase tracking-widest" style={{ color: currentTone.color }}>
                  {currentTone.label}
                </span>
              </div>
              <p className="text-sm font-medium text-gray-700 leading-relaxed">
                "{currentTone.text}"
              </p>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>

      {/* Progress dots */}
      <div className="flex gap-2 mt-5">
        {DEMO_TONES.map((tone, i) => (
          <motion.div key={i}
            className="h-1.5 rounded-full"
            style={{ backgroundColor: i === toneIdx ? tone.color : '#d1d5db' }}
            animate={{ width: i === toneIdx ? 28 : 6 }}
            transition={{ duration: 0.4 }}
          />
        ))}
      </div>
    </div>
  );
}

// ============================================
// CHAT CARD
// ============================================
function ChatCard({ chat, onClick }) {
  const [isHovered, setIsHovered] = useState(false);
  const [visibleMessages, setVisibleMessages] = useState(0);
  const messages = chat?.messages?.slice(0, 6) || [];
  const touchedRef = useRef(false);
  const autoHideRef = useRef(null);

  useEffect(() => {
    if (!isHovered || messages.length === 0) { setVisibleMessages(0); return; }
    setVisibleMessages(1);
    const interval = setInterval(() => { setVisibleMessages(prev => prev < messages.length ? prev + 1 : prev); }, 600);
    return () => clearInterval(interval);
  }, [isHovered, messages.length]);

  // Auto-hide preview after 3s on mobile
  useEffect(() => {
    return () => { if (autoHideRef.current) clearTimeout(autoHideRef.current); };
  }, []);

  const handleClick = () => {
    // If touch-activated hover is showing, second tap navigates
    if (touchedRef.current && isHovered) {
      touchedRef.current = false;
      onClick?.(chat.id);
      return;
    }
    // Desktop click always navigates
    if (!touchedRef.current) {
      onClick?.(chat.id);
    }
  };

  const handleTouchStart = () => {
    if (messages.length === 0) return;
    touchedRef.current = true;
    if (!isHovered) {
      setIsHovered(true);
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
      autoHideRef.current = setTimeout(() => { setIsHovered(false); touchedRef.current = false; }, 3500);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all cursor-pointer overflow-hidden relative h-[220px]"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onMouseEnter={() => { if (!touchedRef.current) setIsHovered(true); }}
      onMouseLeave={() => { if (!touchedRef.current) setIsHovered(false); }}>
      {isHovered && messages.length > 0 && (
        <div className="absolute inset-0 bg-white z-10 overflow-y-auto p-3">
          {messages.slice(0, visibleMessages).map((msg, i) => (
            <div key={i} className={`mb-2 flex items-start gap-2 animate-fadeIn ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
              <ModelLogo modelName={msg.model_name || msg.name} size="sm" />
              <div className={`flex-1 min-w-0 ${i % 2 === 1 ? 'text-right' : ''}`}>
                <p className="text-xs font-semibold text-gray-700 truncate">{msg.model_name || msg.name}</p>
                <p className="text-xs text-gray-600 line-clamp-2" dir={isRTL(msg.content) ? 'rtl' : 'ltr'}>{msg.content}</p>
              </div>
            </div>
          ))}
          {visibleMessages < messages.length && (
            <div className="flex items-center gap-1 text-blue-500 mt-2">
              {[0, 1, 2].map(i => (<span key={i} className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.1}s` }} />))}
            </div>
          )}
        </div>
      )}
      <div className={`h-full flex flex-col transition-opacity ${isHovered && messages.length > 0 ? 'opacity-0' : 'opacity-100'}`}>
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <p className="font-semibold text-gray-800 line-clamp-2 text-sm" dir={isRTL(chat.topic) ? 'rtl' : 'ltr'}>{chat.topic}</p>
        </div>
        {/* Spacer - pushes models to bottom */}
        <div className="flex-1" />
        {/* Models - pinned above footer */}
        <div className="px-3 pb-2 flex flex-wrap gap-1.5 flex-shrink-0">
          {chat.models?.slice(0, 4).map((model, i) => (
            <div key={i} className="flex items-center gap-1.5 bg-gray-50 rounded-full px-2 py-1">
              <ModelLogo modelName={model.name || model} size="xs" />
              <span className="text-xs text-gray-600 truncate max-w-[70px]">{(model.name || model).split(' ')[0]}</span>
            </div>
          ))}
          {chat.models?.length > 4 && <span className="text-xs text-gray-400 px-2 py-1">+{chat.models.length - 4}</span>}
        </div>
        <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between text-xs text-gray-500 flex-shrink-0 border-t border-gray-100">
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1"><MessageCircle size={12} />{chat.total_messages}</span>
            {chat.votes > 0 ? <span className="flex items-center gap-1 text-yellow-600"><Star size={12} fill="currentColor" />{chat.votes}</span> : <span className="flex items-center gap-1 text-gray-300"><Star size={12} />0</span>}
          </div>
          <span className="flex items-center gap-1"><Clock size={12} />{formatTimeAgo(chat.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// COMPARISON CARD
// ============================================
function ComparisonCard({ comparison, onClick }) {
  const [isHovered, setIsHovered] = useState(false);
  const [visibleResults, setVisibleResults] = useState(0);
  const touchedRef = useRef(false);
  const autoHideRef = useRef(null);
  let models = [], results = [];
  if (comparison.results && Array.isArray(comparison.results)) { results = comparison.results; models = results.map(r => r.model_name || r.model || r.model_id || 'Unknown'); }
  else if (comparison.models && Array.isArray(comparison.models)) { models = comparison.models; }
  const prompt = comparison.prompt || comparison.user_prompt || comparison.query || 'No prompt';
  const id = comparison.run_id || comparison.id || comparison._id;
  const category = comparison.category || '';
  const isImageComparison = category.toLowerCase().includes('image') || category.toLowerCase().includes('video');
  const getImageUrl = (r) => r.image_url || r.output_url || r.url || (r.output && r.output.startsWith('http') ? r.output : null);

  useEffect(() => {
    if (!isHovered || results.length === 0) { setVisibleResults(0); return; }
    setVisibleResults(1);
    const interval = setInterval(() => { setVisibleResults(prev => prev < results.length ? prev + 1 : prev); }, 500);
    return () => clearInterval(interval);
  }, [isHovered, results.length]);

  useEffect(() => {
    return () => { if (autoHideRef.current) clearTimeout(autoHideRef.current); };
  }, []);

  const handleClick = () => {
    if (touchedRef.current && isHovered) {
      touchedRef.current = false;
      onClick?.(id);
      return;
    }
    if (!touchedRef.current) onClick?.(id);
  };

  const handleTouchStart = () => {
    if (results.length === 0) return;
    touchedRef.current = true;
    if (!isHovered) {
      setIsHovered(true);
      if (autoHideRef.current) clearTimeout(autoHideRef.current);
      autoHideRef.current = setTimeout(() => { setIsHovered(false); touchedRef.current = false; }, 3500);
    }
  };

  if (!id) return null;

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-lg transition-all cursor-pointer h-[220px] flex flex-col overflow-hidden relative"
      onClick={handleClick}
      onTouchStart={handleTouchStart}
      onMouseEnter={() => { if (!touchedRef.current) setIsHovered(true); }}
      onMouseLeave={() => { if (!touchedRef.current) setIsHovered(false); }}>
      {isHovered && results.length > 0 && (
        <div className="absolute inset-0 bg-white z-10 overflow-y-auto p-3">
          <p className="text-xs text-gray-500 mb-2 font-medium">{isImageComparison ? 'Generated Images:' : 'Responses:'}</p>
          {isImageComparison ? (
            <div className="grid grid-cols-2 gap-2">
              {results.slice(0, visibleResults).map((result, i) => {
                const imageUrl = getImageUrl(result);
                return (<div key={i} className="animate-fadeIn">{imageUrl ? (<div className="relative"><img src={imageUrl} alt="" className="w-full h-20 object-cover rounded-lg" onError={(e) => { e.target.style.display = 'none'; }} /><div className="absolute bottom-0 left-0 right-0 bg-black/60 px-1 py-0.5 rounded-b-lg"><p className="text-[10px] text-white truncate">{result.model_name || result.model}</p></div></div>) : (<div className="h-20 bg-gray-100 rounded-lg flex items-center justify-center"><span className="text-xs text-gray-400">No image</span></div>)}</div>);
              })}
            </div>
          ) : (
            results.slice(0, visibleResults).map((result, i) => (
              <div key={i} className={`mb-2 flex items-start gap-2 animate-fadeIn ${i % 2 === 1 ? 'flex-row-reverse' : ''}`}>
                <ModelLogo modelName={result.model_name || result.model} size="sm" />
                <div className={`flex-1 min-w-0 ${i % 2 === 1 ? 'text-right' : ''}`}>
                  <p className="text-xs font-semibold text-purple-700 truncate">{result.model_name || result.model}</p>
                  <p className="text-xs text-gray-600 line-clamp-2" dir={isRTL(result.output || result.response || result.content || '') ? 'rtl' : 'ltr'}>{result.output || result.response || result.content || 'No response'}</p>
                </div>
              </div>
            ))
          )}
          {visibleResults < results.length && (
            <div className="flex items-center gap-1 text-purple-500 mt-2">
              {[0, 1, 2].map(i => (<span key={i} className="w-1.5 h-1.5 bg-purple-500 rounded-full animate-bounce" style={{ animationDelay: `${i * 0.1}s` }} />))}
            </div>
          )}
        </div>
      )}
      <div className={`h-full flex flex-col transition-opacity ${isHovered && results.length > 0 ? 'opacity-0' : 'opacity-100'}`}>
        {isImageComparison && (<div className="absolute top-2 right-2 z-5"><span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${category.includes('VIDEO') ? 'bg-pink-100 text-pink-600' : 'bg-blue-100 text-blue-600'}`}>{category.includes('VIDEO') ? '🎬 Video' : '🖼️ Image'}</span></div>)}
        <div className="p-4 border-b border-gray-100 flex-shrink-0">
          <p className="font-semibold text-gray-800 line-clamp-2 text-sm" dir={isRTL(prompt) ? 'rtl' : 'ltr'}>{prompt}</p>
        </div>
        {/* Spacer - pushes models to bottom */}
        <div className="flex-1" />
        {/* Models - pinned above footer */}
        <div className="px-3 pb-2 flex flex-wrap gap-1.5 flex-shrink-0">
          {models.length > 0 ? (<>{models.slice(0, 4).map((model, i) => (<div key={i} className="flex items-center gap-1.5 bg-purple-50 rounded-full px-2 py-1"><ModelLogo modelName={model} size="xs" /><span className="text-xs text-purple-600 truncate max-w-[70px]">{String(model).split(' ')[0]}</span></div>))}{models.length > 4 && <span className="text-xs text-gray-400 px-2 py-1">+{models.length - 4}</span>}</>) : <span className="text-xs text-gray-400">No model data</span>}
        </div>
        <div className="px-4 py-2.5 bg-gray-50 flex items-center justify-between text-xs text-gray-500 flex-shrink-0 border-t border-gray-100">
          <div className="flex items-center gap-3">
            {(comparison.total_votes || comparison.votes || 0) > 0 ? (<span className="flex items-center gap-1 text-yellow-600"><Star size={12} fill="currentColor" />{comparison.total_votes || comparison.votes}</span>) : (<span className="flex items-center gap-1 text-gray-300"><Star size={12} />0</span>)}
            {(comparison.user_email || comparison.username) && (<span className="text-gray-500 truncate max-w-[80px]">{comparison.username || comparison.user_email?.split('@')[0] || 'Anonymous'}</span>)}
          </div>
          <span className="flex items-center gap-1"><Clock size={12} />{formatTimeAgo(comparison.created_at)}</span>
        </div>
      </div>
    </div>
  );
}

// ============================================
// HERO CAROUSEL
// ============================================
function HeroCarousel({ currentSlide, setCurrentSlide, chatStats, compareStats, tonelabStats, isUserInteracting, demoChat }) {
  const navigate = useNavigate();
  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: true, align: 'start', duration: 30 });

  useEffect(() => { if (emblaApi) emblaApi.scrollTo(currentSlide); }, [emblaApi, currentSlide]);
  useEffect(() => {
    if (!emblaApi) return;
    const onSelect = () => setCurrentSlide(emblaApi.selectedScrollSnap());
    emblaApi.on('select', onSelect);
    return () => emblaApi.off('select', onSelect);
  }, [emblaApi, setCurrentSlide]);

  useEffect(() => {
    if (isUserInteracting || !emblaApi) return;
    const timer = setInterval(() => emblaApi.scrollNext(), 12000);
    return () => clearInterval(timer);
  }, [emblaApi, isUserInteracting]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  return (
    <div className="relative">
      <div className="overflow-hidden rounded-2xl shadow-sm border border-gray-200/60" ref={emblaRef}>
        <div className="flex">
          {HERO_SLIDES.map((slideData, index) => {
            const isActive = index === currentSlide;
            const stats = slideData.id === 'group-chat' ? chatStats : slideData.id === 'tonelab' ? tonelabStats : compareStats;

            return (
              <div key={slideData.id} className="flex-[0_0_100%] min-w-0">
                {/* FIXED HEIGHT for all slides */}
                <div className={`bg-gradient-to-br ${slideData.gradient} relative overflow-hidden ${HERO_HEIGHT}`}>
                  {/* Blobs */}
                  <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-30 blur-3xl" style={{ background: slideData.glowColor }} />
                  <div className="absolute -bottom-10 -left-10 w-48 h-48 rounded-full opacity-20 blur-3xl" style={{ background: slideData.glowColor }} />

                  {/* Nav arrows - bigger */}
                  <button onClick={(e) => { e.stopPropagation(); scrollPrev(); }}
                    className="absolute left-3 top-1/2 -translate-y-1/2 p-2.5 bg-white/70 hover:bg-white backdrop-blur-sm rounded-full text-gray-600 hover:text-gray-900 transition-all z-10 hidden sm:flex shadow-md hover:shadow-lg hover:scale-110">
                    <ChevronLeft size={22} />
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); scrollNext(); }}
                    className="absolute right-3 top-1/2 -translate-y-1/2 p-2.5 bg-white/70 hover:bg-white backdrop-blur-sm rounded-full text-gray-600 hover:text-gray-900 transition-all z-10 hidden sm:flex shadow-md hover:shadow-lg hover:scale-110">
                    <ChevronRight size={22} />
                  </button>

                  {/* Content - fills the fixed height */}
                  <div className="h-full px-6 sm:px-10 lg:px-14 py-6 relative flex items-center">
                    <div className="w-full grid lg:grid-cols-2 gap-6 lg:gap-8 items-center">
                      {/* Left: Text */}
                      <div className="text-left">
                        <motion.div className={`inline-flex items-center gap-2 ${slideData.badgeBg} rounded-full px-3 py-1 text-[11px] font-semibold mb-3`}
                          initial={{ y: -15, opacity: 0 }} animate={isActive ? { y: 0, opacity: 1 } : { y: -15, opacity: 0 }} transition={{ delay: 0.1, duration: 0.35 }}>
                          {slideData.badge}
                        </motion.div>

                        <motion.h1 className={`text-3xl lg:text-4xl font-extrabold ${slideData.textColor} leading-[1.1] mb-2`}
                          initial={{ opacity: 0, x: -20 }} animate={isActive ? { opacity: 1, x: 0 } : { opacity: 0 }} transition={{ delay: 0.15, duration: 0.4 }}>
                          {slideData.title}
                          <span className={`${slideData.highlightColor} ml-2`}>{slideData.titleHighlight}</span>
                        </motion.h1>

                        <motion.p className="text-base text-gray-600 mb-4 max-w-md leading-relaxed"
                          initial={{ opacity: 0 }} animate={isActive ? { opacity: 1 } : { opacity: 0 }} transition={{ delay: 0.25, duration: 0.35 }}>
                          {slideData.description}
                        </motion.p>

                        <motion.div className="flex flex-wrap gap-3"
                          initial={{ opacity: 0, y: 15 }} animate={isActive ? { opacity: 1, y: 0 } : { opacity: 0 }} transition={{ delay: 0.35, duration: 0.35 }}>
                          {slideData.buttons.map((btn, i) => (
                            <button key={i} onClick={() => navigate(btn.link)}
                              className={`inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl font-semibold text-sm transition-all transform hover:scale-105 shadow-sm ${
                                btn.primary ? slideData.primaryBtn : slideData.secondaryBtn
                              }`}>
                              <btn.icon size={16} />{btn.label}
                            </button>
                          ))}
                        </motion.div>

                        {isActive && stats && (
                          <motion.div className="flex items-center gap-6 mt-4 pt-3 border-t border-gray-200/40"
                            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.5 }}>
                            <div className="flex flex-col">
                              <span className="text-xl font-bold text-gray-900">{stats.models || 8}</span>
                              <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Models</span>
                            </div>
                            <div className="w-px h-8 bg-gray-200/60" />
                            <div className="flex flex-col">
                              <span className="text-xl font-bold text-gray-900">{stats.count || 0}</span>
                              <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">
                                {slideData.id === 'group-chat' ? 'Debates' : slideData.id === 'tonelab' ? 'Remixes' : 'Comparisons'}
                              </span>
                            </div>
                            {stats.messages > 0 && (
                              <>
                                <div className="w-px h-8 bg-gray-200/60" />
                                <div className="flex flex-col">
                                  <span className="text-xl font-bold text-gray-900">{stats.messages}</span>
                                  <span className="text-[11px] text-gray-500 uppercase tracking-wider font-semibold">Messages</span>
                                </div>
                              </>
                            )}
                          </motion.div>
                        )}
                      </div>

                      {/* Right: Demo - fills available height */}
                      <motion.div
                        className="hidden lg:flex justify-center items-center"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={isActive ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
                        transition={{ delay: 0.2, duration: 0.5, type: 'spring', stiffness: 80 }}
                      >
                        {slideData.id === 'group-chat' && <ChatBrowserDemo chat={demoChat} isActive={isActive} />}
                        {slideData.id === 'compare' && <CompareSplitBeamDemo isActive={isActive} />}
                        {slideData.id === 'tonelab' && <ToneLiquidDemo isActive={isActive} />}
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Indicators */}
      <div className="flex items-center justify-center gap-2.5 mt-4">
        {HERO_SLIDES.map((s, i) => (
          <button key={i} onClick={() => emblaApi?.scrollTo(i)}
            className={`h-2 rounded-full transition-all duration-500 ${
              i === currentSlide
                ? `w-9 ${s.accentColor === 'blue' ? 'bg-blue-500' : s.accentColor === 'purple' ? 'bg-purple-500' : 'bg-rose-500'}`
                : 'bg-gray-300 hover:bg-gray-400 w-2'
            }`} />
        ))}
      </div>
    </div>
  );
}

// ============================================
// MAIN HOME
// ============================================
export default function Home() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isUserInteracting, setIsUserInteracting] = useState(false);
  const interactionTimeoutRef = useRef(null);
  const [recentChats, setRecentChats] = useState([]);
  const [recentComparisons, setRecentComparisons] = useState([]);
  const [chatStats, setChatStats] = useState({ models: 8, count: 0, messages: 0 });
  const [compareStats, setCompareStats] = useState({ models: 8, count: 0 });
  const [recentToneLabs, setRecentToneLabs] = useState([]);
  const [tonelabStats, setTonelabStats] = useState({ models: 6, count: 0 });
  const [demoChat, setDemoChat] = useState(null);
  const slide = HERO_SLIDES[currentSlide];

  const handleUserInteraction = useCallback(() => {
    setIsUserInteracting(true);
    if (interactionTimeoutRef.current) clearTimeout(interactionTimeoutRef.current);
    interactionTimeoutRef.current = setTimeout(() => setIsUserInteracting(false), 20000);
  }, []);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      // STATIC MODE: load from local JSON files
      const [chatStatsRes, chatsRes, demoChatRes, resultsRes, toneStatsRes, toneRes] = await Promise.all([
        fetch('/data/chat-stats.json').catch(() => null),
        fetch('/data/chats.json').catch(() => null),
        fetch('/data/demo-chat.json').catch(() => null),
        fetch('/data/results.json').catch(() => null),
        fetch('/data/tone-stats.json').catch(() => null),
        fetch('/data/tonelab.json').catch(() => null),
      ]);

      if (chatStatsRes?.ok) {
        const stats = await chatStatsRes.json();
        setChatStats({ models: stats.chats?.models || 8, count: stats.chats?.count || 0, messages: stats.chats?.messages || 0 });
        setCompareStats({ models: stats.comparisons?.models || 8, count: stats.comparisons?.count || 0 });
      }
      if (chatsRes?.ok) { const data = await chatsRes.json(); setRecentChats((data.chats || []).slice(0, 6)); }
      if (demoChatRes?.ok) { setDemoChat(await demoChatRes.json()); }
      if (resultsRes?.ok) { const data = await resultsRes.json(); setRecentComparisons((Array.isArray(data) ? data : data.results || []).slice(0, 6)); }
      if (toneStatsRes?.ok) { const s = await toneStatsRes.json(); setTonelabStats({ models: 6, count: s.total_runs || 0 }); }
      if (toneRes?.ok) { const d = await toneRes.json(); setRecentToneLabs((d.runs || []).slice(0, 6)); }
    } catch (err) { console.error('Failed to load data:', err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChatClick = (chatId) => { trackEvent('Home', 'CardClick', 'Chat'); navigate(`/chat/${chatId}`); };
  const handleComparisonClick = (runId) => { trackEvent('Home', 'CardClick', 'Compare'); navigate(`/compare?highlight=${runId}`); };
  const handleToneLabClick = (runId) => { trackEvent('Home', 'CardClick', 'Tone'); navigate(`/tonelab/${runId}`); };

  if (loading) return (<div className="min-h-screen bg-gray-50 flex items-center justify-center"><Loader className="animate-spin text-blue-500" size={32} /></div>);

  const slideId = slide.id;
  const items = slideId === 'group-chat' ? recentChats : slideId === 'tonelab' ? recentToneLabs : recentComparisons;
  const emptyIcon = slideId === 'group-chat' ? MessageCircle : slideId === 'tonelab' ? FlaskConical : Swords;
  const emptyText = slideId === 'group-chat' ? 'No debates yet' : slideId === 'tonelab' ? 'No AI Tone runs yet' : 'No comparisons yet';
  const emptyButtonText = slideId === 'group-chat' ? 'Start First Debate' : slideId === 'tonelab' ? 'Try AI Tone' : 'Start First Comparison';
  const emptyButtonLink = slideId === 'group-chat' ? '/group-chat?tab=new' : slideId === 'tonelab' ? '/tonelab' : '/compare?tab=new';
  const iconColor = slideId === 'group-chat' ? 'text-blue-500' : slideId === 'tonelab' ? 'text-rose-500' : 'text-purple-500';
  const linkColor = slideId === 'group-chat' ? 'text-blue-500 hover:text-blue-600' : slideId === 'tonelab' ? 'text-rose-500 hover:text-rose-600' : 'text-purple-500 hover:text-purple-600';
  const buttonColor = slideId === 'group-chat' ? 'bg-blue-500 hover:bg-blue-600' : slideId === 'tonelab' ? 'bg-rose-500 hover:bg-rose-600' : 'bg-purple-500 hover:bg-purple-600';

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="px-4 sm:px-0 pt-4">
        <div className="max-w-6xl mx-auto px-0 sm:px-6">
          <HeroCarousel currentSlide={currentSlide} setCurrentSlide={(i) => { setCurrentSlide(i); handleUserInteraction(); }} chatStats={chatStats} compareStats={compareStats} tonelabStats={tonelabStats} isUserInteracting={isUserInteracting} demoChat={demoChat} />
        </div>
      </div>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-lg font-bold text-gray-800 flex items-center gap-2"><slide.sectionIcon className={iconColor} size={22} />{slide.sectionTitle}</h2>
            <button onClick={() => navigate(slide.viewAllLink)} className={`${linkColor} text-sm font-medium flex items-center gap-1`}>View All <ChevronRight size={16} /></button>
          </div>
          {items.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-xl border border-gray-200">
              {React.createElement(emptyIcon, { className: "mx-auto text-gray-300 mb-3", size: 40 })}
              <p className="text-gray-500">{emptyText}</p>
              <button onClick={() => navigate(emptyButtonLink)} className={`mt-3 px-4 py-2 ${buttonColor} text-white rounded-lg transition-colors text-sm`}>{emptyButtonText}</button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {slideId === 'group-chat' ? items.map(chat => <ChatCard key={chat.id} chat={chat} onClick={handleChatClick} />)
              : slideId === 'tonelab' ? items.map(run => <ToneLabCard key={run.id} run={run} onClick={handleToneLabClick} />)
              : items.map(comp => <ComparisonCard key={comp.run_id || comp.id} comparison={comp} onClick={handleComparisonClick} />)}
            </div>
          )}
        </section>
      </div>
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
        .animate-fadeIn { animation: fadeIn 0.25s ease-out forwards; }
      `}</style>
    </div>
  );
}